import { useEffect, useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import Lenis from "lenis";
import Seo from "@/components/Seo";

const LAST_UPDATED = "September 9, 2026";
const EMAIL = "privacy@blackdiamondfacility.ca";

const CHAPTERS = [
  {
    id: "who-we-are",
    num: "01",
    title: "Who we are",
    blocks: [
      {
        type: "p",
        text: "BLACK DIAMOND FACILITY SERVICES (\u201cBlack Diamond\u201d, \u201cwe\u201d, \u201cus\u201d) operates the Black Diamond mobile app and the website blackdiamondfacility.ca (together, the \u201cService\u201d). The Service lets you request and manage facility-service bookings for your properties.",
      },
      {
        type: "p",
        text: "Our privacy contact for every question, request, or complaint is privacy@blackdiamondfacility.ca.",
      },
    ],
  },
  {
    id: "information-we-collect",
    num: "02",
    title: "Information we collect",
    blocks: [
      {
        type: "sub",
        text: "Account information",
      },
      {
        type: "p",
        text: "When you create an account you sign in with Google or with Apple. We receive your name, email address, and \u2014 if your sign-in provider shares it \u2014 a profile photo. Apple users may choose a private relay email address. You may also add a phone number to your profile.",
      },
      {
        type: "sub",
        text: "Booking information",
      },
      {
        type: "p",
        text: "When you request a booking \u2014 with or without an account \u2014 we collect your name, email address, phone number, the service address and its structured address details, the service requested, your preferred date and time, checkout and check-in times, property details such as bedrooms and bathrooms, access instructions, supply notes, preferences, an optional listing link, free-text notes, and up to eight photos you choose to attach.",
      },
      {
        type: "sub",
        text: "Saved properties",
      },
      {
        type: "p",
        text: "If you save properties to your account, we store their label, address, property details, access instructions, preferences, and photos so repeat bookings are faster.",
      },
      {
        type: "sub",
        text: "Push notification registration",
      },
      {
        type: "p",
        text: "If you enable notifications, we register your device platform (iOS or Android) and a push notification token so booking updates can reach your device.",
      },
      {
        type: "callout",
        text: "We do not collect or store passwords \u2014 sign-in works exclusively through Google or Apple. We do not collect payment card data, and the app contains no analytics, advertising, or tracking software.",
      },
    ],
  },
  {
    id: "how-we-use",
    num: "03",
    title: "How we use your information",
    blocks: [
      {
        type: "list",
        items: [
          "To create and secure your account and sign you in",
          "To receive, schedule, confirm, and manage your bookings",
          "To email you booking confirmations and service updates, and to notify our team of new bookings",
          "To send push notifications about your bookings \u2014 you can turn these off in the app or in your device settings",
          "To provide address autocomplete while you enter a service address",
          "To fulfil data export and account deletion requests",
          "To keep security and audit logs that protect the Service from fraud and abuse",
        ],
      },
    ],
  },
  {
    id: "third-party-services",
    num: "04",
    title: "Third-party services we use",
    blocks: [
      {
        type: "p",
        text: "We share data only with the providers required to run the Service:",
      },
      {
        type: "list",
        items: [
          "Google Sign-In and Sign in with Apple \u2014 authentication. We never see your Google or Apple password, and your use of those services is governed by Google\u2019s and Apple\u2019s own privacy policies.",
          "Google Places API \u2014 address autocomplete. Address queries are sent from our server, not your device, and no account identity is attached to them.",
          "Resend \u2014 delivery of transactional email (booking confirmations and updates) from Black Diamond\u2019s own verified domain, with Emergent\u2019s managed email service as a fallback sender.",
          "Emergent (emergent.sh) \u2014 application hosting and managed infrastructure: the authentication session exchange, the push notification relay (device tokens are registered with this relay and delivered through Apple Push Notification service or Firebase Cloud Messaging; our own servers do not retain device tokens), and object storage for the photos you upload.",
          "MongoDB \u2014 the database, operated with the application, where account, booking, and property records are stored.",
        ],
      },
      {
        type: "callout",
        text: "We do not sell your personal information.",
      },
    ],
  },
  {
    id: "export-and-deletion",
    num: "05",
    title: "Data export and account deletion",
    blocks: [
      {
        type: "sub",
        text: "Export",
      },
      {
        type: "p",
        text: "You can download a copy of your records from within the app at any time, in PDF or JSON format. The export covers your account details, saved properties, and booking history; attached photos are listed by count rather than embedded.",
      },
      {
        type: "sub",
        text: "Deletion",
      },
      {
        type: "p",
        text: "You can permanently delete your account in the app, confirmed by typing DELETE. Deletion removes your user record, your saved properties, and your active sign-in sessions. Your past booking and customer records are anonymized: your name, email address, phone number, and address are replaced with placeholder values, and for completed bookings the access instructions, notes, photos, and address details are erased. Uploaded photos that are no longer referenced by any record are purged from storage.",
      },
      {
        type: "p",
        text: "Limited, pseudonymous audit records of the deletion itself are kept for security and fraud-prevention purposes. You can also email privacy@blackdiamondfacility.ca to request access, correction, export, or deletion.",
      },
    ],
  },
  {
    id: "retention-and-backups",
    num: "06",
    title: "Data retention and backups",
    blocks: [
      {
        type: "p",
        text: "We keep your account and booking data while your account is active. When you delete your account, personal information is deleted or anonymized as described above, subject to records we must retain for legal, accounting, security, fraud-prevention, or dispute-resolution purposes.",
      },
      {
        type: "p",
        text: "We take a verified backup of the database every night. Backups are kept on a rotating schedule \u2014 nightly backups for 7 days, weekly backups for 8 weeks, and monthly backups for up to 12 months. Information you delete from the live app may therefore remain in backups until its rotation window passes. Backups are used only for disaster recovery and are not used to return deleted personal information to live systems.",
      },
    ],
  },
  {
    id: "security",
    num: "07",
    title: "Security",
    blocks: [
      {
        type: "p",
        text: "All traffic between the app and our servers uses HTTPS encryption. Sign-in sessions use server-side tokens that expire after 7 days, and no passwords are ever stored. Uploaded photos are validated by file type, and the credentials used for address lookup never leave our server. No method of transmission or storage is perfectly secure, but we work to protect your information with these measures and limit access to it.",
      },
    ],
  },
  {
    id: "children",
    num: "08",
    title: "Children",
    blocks: [
      {
        type: "p",
        text: "The Service is not directed at children, and we do not knowingly collect personal information from children. If you believe a child has provided us personal information, contact privacy@blackdiamondfacility.ca and we will remove it.",
      },
    ],
  },
  {
    id: "where-data-is-processed",
    num: "09",
    title: "Where data is processed",
    blocks: [
      {
        type: "p",
        text: "Your information is processed by the providers listed in Chapter 04, in the regions where those providers operate. By using the Service, you understand that your information may be processed outside your home province, state, or country.",
      },
    ],
  },
  {
    id: "changes",
    num: "10",
    title: "Changes to this policy",
    blocks: [
      {
        type: "p",
        text: "We may update this policy from time to time. The current version will always be posted at blackdiamondfacility.ca/privacy with its \u201cLast updated\u201d date, and material changes will be reflected there before they take effect.",
      },
    ],
  },
  {
    id: "contact",
    num: "11",
    title: "Contact",
    blocks: [
      {
        type: "p",
        text: "The Service is operated by BLACK DIAMOND FACILITY SERVICES. For any privacy question, request, or complaint \u2014 including access, correction, export, or deletion of your personal information \u2014 write to us at privacy@blackdiamondfacility.ca.",
      },
    ],
  },
];

const EASE = [0.16, 1, 0.3, 1];

const DiamondMark = ({ size = 34, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    aria-hidden="true"
    {...props}
  >
    <defs>
      <linearGradient id="bd-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#F2F2F7" />
        <stop offset="0.5" stopColor="#8E8E93" />
        <stop offset="1" stopColor="#1C1C1E" />
      </linearGradient>
    </defs>
    <path
      d="M50 6 L92 40 L50 94 L8 40 Z"
      fill="url(#bd-g)"
      stroke="#F2F2F7"
      strokeWidth="1.5"
    />
    <path d="M50 6 L68 40 L50 94 L32 40 Z" fill="#050505" opacity="0.35" />
    <path d="M8 40 L92 40" stroke="#F2F2F7" strokeWidth="1" opacity="0.6" />
    <path
      d="M32 40 L50 6 L68 40"
      stroke="#F2F2F7"
      strokeWidth="0.8"
      opacity="0.5"
      fill="none"
    />
  </svg>
);

const Grain = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none fixed inset-0 z-50 opacity-[0.055] mix-blend-screen"
    style={{
      backgroundImage:
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
    }}
  />
);

const MonoLabel = ({ children, className = "", ...props }) => (
  <span
    className={`font-mono text-[11px] uppercase tracking-[0.28em] text-[#8E8E93] ${className}`}
    {...props}
  >
    {children}
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), {
    stiffness: 50,
    damping: 16,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), {
    stiffness: 50,
    damping: 16,
  });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const gemY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const gemSpin = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const line = (text, i, italic = false, testId) => (
    <div key={text} className="overflow-hidden py-[0.5vw]">
      <motion.span
        data-testid={testId}
        className={`block font-serif text-[16vw] leading-[0.95] text-[#F2F2F7] sm:text-[13vw] lg:text-[10.5vw] ${
          italic ? "italic text-[#A1A1A6]" : ""
        }`}
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.15, ease: EASE, delay: 0.25 + i * 0.14 }}
      >
        {text}
      </motion.span>
    </div>
  );

  return (
    <header
      ref={ref}
      onMouseMove={onMove}
      className="relative flex min-h-[92vh] flex-col justify-end overflow-hidden px-6 pb-16 pt-32 sm:px-10 lg:px-16"
    >
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute right-[4%] top-[16%] opacity-90 sm:right-[10%]"
        style={{
          y: gemY,
          rotate: gemSpin,
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
          perspective: 800,
        }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.9, scale: 1 }}
        transition={{ duration: 1.6, ease: EASE, delay: 0.9 }}
      >
        <DiamondMark size={190} />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          className="mb-8 flex flex-wrap items-center gap-x-6 gap-y-2"
        >
          <MonoLabel data-testid="hero-kicker">Black Diamond — Facility Service</MonoLabel>
          <span className="hidden h-px w-16 bg-[#2C2C2E] sm:block" />
          <MonoLabel>Legal Document</MonoLabel>
        </motion.div>

        <h1 className="m-0">
          {line("Privacy", 0, false, "hero-title-line-privacy")}
          {line("Policy", 1, true, "hero-title-line-policy")}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
          className="mt-10 flex flex-col gap-3 border-t border-[#2C2C2E] pt-6 sm:flex-row sm:items-end sm:justify-between"
        >
          <p
            data-testid="hero-scope"
            className="max-w-md text-sm leading-relaxed text-[#A1A1A6]"
          >
            This policy explains what the Black Diamond app collects, why, and
            the control you have over it — in plain language, matched to what
            the app actually does.
          </p>
          <MonoLabel data-testid="hero-last-updated">
            Last updated — {LAST_UPDATED}
          </MonoLabel>
        </motion.div>
      </motion.div>
    </header>
  );
};

const MARQUEE_ITEMS = [
  "No passwords stored",
  "No tracking",
  "No advertising",
  "No sale of data",
  "Export anytime",
  "Delete anytime",
  "Clean work, reliable service",
];

const Marquee = () => (
  <div
    data-testid="editorial-marquee"
    className="overflow-hidden border-y border-[#2C2C2E] py-5"
    aria-hidden="true"
  >
    <div className="marquee-track flex w-max items-center">
      {[0, 1].map((copy) => (
        <div key={copy} className="flex shrink-0 items-center">
          {MARQUEE_ITEMS.map((item) => (
            <span key={`${copy}-${item}`} className="flex items-center">
              <span className="px-8 font-serif text-2xl italic text-[#636366] sm:text-3xl">
                {item}
              </span>
              <span className="inline-block h-2 w-2 rotate-45 bg-[#3A3A3C]" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const Block = ({ block }) => {
  if (block.type === "sub") {
    return (
      <h3 className="mt-10 font-serif text-xl text-[#F2F2F7]">{block.text}</h3>
    );
  }
  if (block.type === "callout") {
    return (
      <div className="mt-10 border-l-2 border-[#F2F2F7] bg-[#0C0C0C] px-6 py-5">
        <p className="m-0 text-sm leading-relaxed text-[#F2F2F7]">
          {block.text}
        </p>
      </div>
    );
  }
  if (block.type === "list") {
    return (
      <ul className="m-0 mt-6 list-none space-y-4 p-0">
        {block.items.map((item) => (
          <li key={item.slice(0, 40)} className="flex gap-4">
            <span
              aria-hidden="true"
              className="mt-[9px] h-1.5 w-1.5 shrink-0 rotate-45 bg-[#8E8E93]"
            />
            <span className="text-sm leading-relaxed text-[#A1A1A6]">
              {item}
            </span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p className="m-0 mt-6 text-sm leading-relaxed text-[#A1A1A6] first:mt-0 sm:text-base">
      {block.text}
    </p>
  );
};

const Chapter = ({ chapter }) => (
  <motion.section
    id={chapter.id}
    data-testid={`chapter-${chapter.id}`}
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-90px" }}
    transition={{ duration: 0.9, ease: EASE }}
    className="relative scroll-mt-28 border-t border-[#1C1C1E] py-20 sm:py-28"
  >
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -top-6 right-0 select-none font-serif text-[26vw] leading-none text-[#0C0C0C] sm:text-[18vw] lg:text-[13rem]"
    >
      {chapter.num}
    </span>
    <div className="relative max-w-2xl">
      <MonoLabel>Chapter {chapter.num}</MonoLabel>
      <h2 className="mb-8 mt-3 font-serif text-3xl text-[#F2F2F7] sm:text-4xl">
        {chapter.title}
      </h2>
      {chapter.blocks.map((block, i) => (
        <Block key={`${chapter.id}-${i}`} block={block} />
      ))}
    </div>
  </motion.section>
);

const ChapterRail = ({ onNavigate }) => (
  <nav
    data-testid="chapter-rail"
    aria-label="Chapters"
    className="sticky top-28 hidden self-start lg:block"
  >
    <MonoLabel className="mb-6 block">Index</MonoLabel>
    <ul className="m-0 list-none space-y-3 p-0">
      {CHAPTERS.map((c) => (
        <li key={c.id}>
          <a
            data-testid={`chapter-link-${c.id}`}
            href={`#${c.id}`}
            onClick={(e) => onNavigate(e, c.id)}
            className="group flex items-baseline gap-3 text-[#636366] transition-colors duration-300 hover:text-[#F2F2F7]"
          >
            <span className="font-mono text-[10px] tracking-[0.2em]">
              {c.num}
            </span>
            <span className="text-[13px]">{c.title}</span>
            <span className="h-px w-0 bg-[#F2F2F7] transition-all duration-300 group-hover:w-6" />
          </a>
        </li>
      ))}
    </ul>
  </nav>
);

export default function Privacy() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const scrollToChapter = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    const lenis = lenisRef.current;
    if (!el || !lenis) return;
    lenis.resize();
    const y = el.getBoundingClientRect().top + window.scrollY - 96;
    lenis.scrollTo(y, { duration: 1.4, force: true });
  };

  return (
    <div
      data-testid="privacy-page"
      className="min-h-screen bg-[#050505] text-[#F2F2F7] antialiased"
      style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
    >
      <Seo
        title="Privacy Policy — Black Diamond"
        siteName="Black Diamond"
        description="What the Black Diamond facility-service app collects, why, and how to export or delete your data. No tracking, no ads, no sale of personal information."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Black Diamond",
          url: "https://blackdiamondfacility.ca",
          email: EMAIL,
        }}
      />
      <Grain />

      <nav
        data-testid="privacy-nav"
        className="fixed inset-x-0 top-0 z-40 flex items-center justify-between border-b border-[#1C1C1E] bg-[#050505]/80 px-6 py-4 backdrop-blur-md sm:px-10 lg:px-16"
      >
        <a
          data-testid="nav-brand-link"
          href="https://blackdiamondfacility.ca"
          className="group flex items-center gap-3"
        >
          <span className="transition-transform duration-500 group-hover:rotate-90">
            <DiamondMark size={26} />
          </span>
          <span className="leading-none">
            <span className="block font-mono text-[11px] tracking-[0.32em] text-[#F2F2F7]">
              BLACK DIAMOND
            </span>
            <span className="mt-1 block font-mono text-[8px] tracking-[0.4em] text-[#636366]">
              FACILITY SERVICE
            </span>
          </span>
        </a>
        <div className="flex items-center gap-6">
          <a
            data-testid="nav-terms-link"
            href="/terms"
            className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8E8E93] transition-colors duration-300 hover:text-[#F2F2F7]"
          >
            Terms
          </a>
          <MonoLabel data-testid="nav-updated" className="hidden sm:block">
            {LAST_UPDATED}
          </MonoLabel>
        </div>
      </nav>

      <Hero />
      <Marquee />

      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 pb-24 sm:px-10 lg:grid-cols-[240px_1fr] lg:px-16">
        <div className="pt-20 sm:pt-28">
          <ChapterRail onNavigate={scrollToChapter} />
        </div>
        <div>
          {CHAPTERS.map((c) => (
            <Chapter key={c.id} chapter={c} />
          ))}
        </div>
      </main>

      <footer
        data-testid="privacy-footer"
        className="border-t border-[#1C1C1E] px-6 py-24 sm:px-10 lg:px-16"
      >
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <MonoLabel>Questions about your data</MonoLabel>
          <a
            data-testid="footer-email-link"
            href={`mailto:${EMAIL}`}
            className="mt-6 inline-block max-w-full break-all font-serif text-[7.5vw] leading-tight text-[#F2F2F7] underline decoration-[#3A3A3C] decoration-1 underline-offset-8 transition-colors duration-300 hover:decoration-[#F2F2F7] sm:text-5xl"
          >
            {EMAIL}
          </a>
          <div className="mt-20 flex flex-col gap-4 border-t border-[#1C1C1E] pt-8 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <DiamondMark size={22} />
              <MonoLabel>Black Diamond — Facility Service</MonoLabel>
            </div>
            <MonoLabel data-testid="footer-tagline">
              Clean work, reliable service.
            </MonoLabel>
          </div>
        </motion.div>
      </footer>
    </div>
  );
}
